import React, { RefObject, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Platform } from 'react-native';

import { AutocompleteAddressInput } from 'components/autocomplete-address-input';
import { FormValidationState } from 'utils/form';
import { IPlaceAddress } from 'utils/geolocation';

import { IPhoneAddressValidation } from '../types';

interface IDeliveryAddressFormInputProps {
  addressString: string;
  deliveryAddressInputRef?: React.RefObject<HTMLInputElement>;
  message: string;
  setAddressComponents: (address: IPlaceAddress | null) => void;
  setAddressString: (address: string) => void;
  setCoordinates: (coordinates: undefined) => void;
  setMessage: (message: string) => void;
  setPlaceId: (placeId: string) => void;
  setValidation: (validation: IPhoneAddressValidation) => void;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  unitRef: RefObject<HTMLInputElement>;
  validation: IPhoneAddressValidation;
  initialFocus?: boolean;
  /** Focus the delivery address search field after mount (e.g. returning from delivery unavailable). */
  shouldFocusInput?: boolean;
  onInputFocused?: () => void;
}

function focusDeliveryAddressInput(inputRef: React.RefObject<HTMLInputElement>) {
  if (!inputRef.current) {
    return;
  }

  if (Platform.OS === 'web') {
    const wrapper = inputRef.current as HTMLElement;
    const inputEl =
      wrapper.tagName === 'INPUT'
        ? (wrapper as HTMLInputElement)
        : wrapper.querySelector<HTMLInputElement>('input');
    inputEl?.focus();
    return;
  }

  inputRef.current.focus();
}

const DeliveryAddressFormInput: React.FC<
  React.PropsWithChildren<IDeliveryAddressFormInputProps>
> = ({
  setAddressString,
  setPlaceId,
  setValidation,
  setMessage,
  unitRef,
  validation,
  setCoordinates,
  setAddressComponents,
  addressString,
  deliveryAddressInputRef,
  message,
  setValue,
  initialFocus = false,
  shouldFocusInput = false,
  onInputFocused,
}) => {
  const { formatMessage } = useIntl();
  const deliveryAddressLabel = formatMessage({ id: 'deliveryAddress' });

  const defaultAddressInputRef = useRef<HTMLInputElement>(null);
  const hadNonEmptyAddressString = useRef(false);

  const AddressInputRef = deliveryAddressInputRef ?? defaultAddressInputRef;

  const handleAddressChange = (newAddressString: string, placeId?: string) => {
    setAddressString(newAddressString);
    setMessage('');

    if (!(newAddressString ?? '').trim()) {
      setValidation({
        ...validation,
        address: FormValidationState.PRISTINE,
      });
      setPlaceId('');
      setAddressComponents(null);
      setCoordinates(undefined);
      return;
    }

    if (!placeId) {
      setValidation({
        ...validation,
        address: FormValidationState.INVALID,
      });
      setMessage(formatMessage({ id: 'addressError' }));
      setAddressComponents(null);
      return;
    }

    if (unitRef.current) {
      unitRef.current.focus();
    }

    setValidation({
      ...validation,
      address: FormValidationState.PRISTINE,
    });
    setPlaceId(placeId);
    setCoordinates(undefined);
  };

  useEffect(() => {
    if (!shouldFocusInput) {
      return;
    }

    requestAnimationFrame(() => {
      focusDeliveryAddressInput(AddressInputRef);
      onInputFocused?.();
    });
  }, [shouldFocusInput, onInputFocused, AddressInputRef]);

  useEffect(() => {
    if (addressString) {
      hadNonEmptyAddressString.current = true;
    }
    if (!addressString && AddressInputRef.current) {
      // Web: initial screen focus is moved to the modal close control; only refocus after the user clears text.
      if (Platform.OS === 'web' && !hadNonEmptyAddressString.current) {
        return;
      }
      // AddressInputRef points to the NativeBase wrapper element, not the inner <input>.
      // Query for the actual focusable input so focus is restored correctly on web.
      if (Platform.OS === 'web') {
        const wrapper = AddressInputRef.current as HTMLElement;
        const inputEl =
          wrapper.tagName === 'INPUT'
            ? (wrapper as HTMLInputElement)
            : wrapper.querySelector<HTMLInputElement>('input');
        inputEl?.focus();
      } else {
        AddressInputRef.current.focus();
      }
    }
  }, [addressString, AddressInputRef]);

  return (
    <AutocompleteAddressInput
      data-private="redact"
      testID="delivery-address-input"
      label={deliveryAddressLabel}
      addressesOnly
      message={message}
      onAddressSelected={handleAddressChange}
      // @ts-expect-error TS(2322) FIXME: Type 'Dispatch<SetStateAction<string>>' is not ass... Remove this comment to see the full error message
      onInputValueChange={setValue}
      ref={AddressInputRef}
      required
      // @ts-expect-error TS(2322) FIXME: Type '(message: string) => void' is not assignable... Remove this comment to see the full error message
      setMessage={setMessage}
      value={addressString}
      initialFocus={initialFocus}
    />
  );
};

export default DeliveryAddressFormInput;
